import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Bot, Info } from "lucide-react";

export default function AdminChatbot() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data));
    api.get("/courses").then(({ data }) => setCourses(data));
  }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(data));
    const parts = path.split(".");
    let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setData(clone);
  };
  const save = async () => { try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); } catch { toast.error("Save failed"); } };
  const setQuickReplies = (str) => set("chatbot_config.quick_replies", str.split(",").map((s) => s.trim()).filter(Boolean));

  const F = ({ label, path, type = "text", rows }) => {
    const parts = path.split(".");
    let val = data; for (const p of parts) val = val?.[p] ?? "";
    const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
    if (type === "textarea") return (<div><label className="text-xs font-semibold text-neutral-600">{label}</label><textarea rows={rows || 3} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`chatbot-${path}`} /></div>);
    if (type === "checkbox") return (<label className="flex items-center gap-2"><input type="checkbox" checked={!!val} onChange={(e) => set(path, e.target.checked)} data-testid={`chatbot-${path}`} /><span className="text-sm">{label}</span></label>);
    return (<div><label className="text-xs font-semibold text-neutral-600">{label}</label><input type={type} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`chatbot-${path}`} /></div>);
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center"><Bot className="w-5 h-5 text-white" /></div>
            <h1 className="font-display font-black text-3xl" data-testid="admin-chatbot-title">AI Chatbot</h1>
          </div>
          <p className="text-neutral-500 mt-2">Customize the chatbot's voice, appearance, and lead flow. Courses are read live from the Course Manager.</p>
        </div>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="chatbot-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Identity</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Bot name" path="chatbot_config.bot_name" />
            <F label="Avatar image URL" path="chatbot_config.avatar_url" />
            <F label="Welcome message" path="chatbot_config.greeting" type="textarea" rows={2} />
            <F label="Consent text" path="chatbot_config.consent_text" type="textarea" rows={2} />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Appearance</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Primary colour (hex)" path="chatbot_config.primary_color" />
            <div>
              <label className="text-xs font-semibold text-neutral-600">Position</label>
              <select value={data.chatbot_config?.position || "bottom-right"} onChange={(e) => set("chatbot_config.position", e.target.value)} className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm">
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-neutral-600">Quick reply chips (comma-separated)</label>
            <input value={(data.chatbot_config?.quick_replies || []).join(", ")} onChange={(e) => setQuickReplies(e.target.value)}
                   className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid="chatbot-quick-replies" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Conversation Flow</h2>
          <F label="Course question wording" path="chatbot_config.question_course" />
          <div className="mt-4"><F label="Ask email" path="chatbot_config.ask_email" type="checkbox" /></div>
          <F label="Ask city" path="chatbot_config.ask_city" type="checkbox" />
          <div className="mt-4"><F label="Course-response template" path="chatbot_config.course_response_template" type="textarea" rows={3} /></div>
          <F label="Syllabus button text" path="chatbot_config.syllabus_button_text" />
          <F label="Human-handover text (button on chatbot panel)" path="chatbot_config.handover_text" />
          <F label="Human support number (WhatsApp)" path="chatbot_config.human_support_number" />
          <F label="Working hours" path="chatbot_config.working_hours" />
          <F label="Offline message" path="chatbot_config.offline_message" type="textarea" rows={2} />
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Duplicate prevention</h2>
          <F label="Deduplicate leads on phone number" path="chatbot_config.dedupe_phone" type="checkbox" />
          <F label="Deduplicate leads on email" path="chatbot_config.dedupe_email" type="checkbox" />
        </div>

        <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
          <div className="text-sm text-neutral-700">
            <div className="font-semibold">Live-course data</div>
            <p className="mt-1">The chatbot always reads the {courses.length} published courses in real time. To change offered courses, edit the <b>Courses</b> section — no chatbot code changes needed.</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {courses.map((c) => <span key={c.id} className="text-xs px-2 py-1 rounded-full bg-white border border-sky-200 text-sky-700">{c.title}</span>)}
            </div>
          </div>
        </div>

        <F label="Chatbot enabled" path="chatbot_config.enabled" type="checkbox" />
      </div>
    </div>
  );
}
